import type {
  ActivityType,
  CandleBucketSize,
  CategoryIdentifier,
  ContractStandard,
  EditableCollectionsStatus,
  EditableCollectionsType,
  OfferStatus,
  OfferType,
  OrderStatus,
  ProfileOffersView,
  Timeframe,
} from "@opensea/graphql"
import { useTranslations } from "next-intl"

export const useActivityEnumName = () => {
  const t = useTranslations("useEnumName")

  return (type: ActivityType | "All" | "PRIVATE_LISTING") => {
    switch (type) {
      case "All":
        return t("all")
      case "SALE":
        return t("sale")
      case "LISTING":
        return t("listing")
      case "PRIVATE_LISTING":
        return t("privateListing")
      case "OFFER":
        return t("offer")
      case "COLLECTION_OFFER":
        return t("collectionOffer")
      case "MINT":
        return t("mint")
      case "TRAIT_OFFER":
        return t("traitOffer")
      case "TRANSFER":
        return t("transfer")
    }
  }
}

export const useOrderStatusEnumName = () => {
  const t = useTranslations("useEnumName")

  return (status: OrderStatus) => {
    switch (status) {
      case "ACTIVE":
        return t("active")
      case "INACTIVE":
        return t("inactive")
    }
  }
}

export const useOfferStatusEnumName = () => {
  const t = useTranslations("useEnumName")

  return (status: OfferStatus) => {
    switch (status) {
      case "NONE":
        return t("none")
      case "ACTIVE":
        return t("active")
      case "CANCELLED":
        return t("cancelled")
      case "COMPLETED":
        return t("completed")
      case "EXPIRED":
        return t("expired")
      case "UNFUNDED":
        return t("unfunded")
    }
  }
}

export const useProfileOffersViewEnumName = () => {
  const t = useTranslations("useEnumName")

  return (view: ProfileOffersView) => {
    switch (view) {
      case "MADE":
        return t("offersMade")
      case "RECEIVED":
        return t("offersReceived")
    }
  }
}

export const useCategoryEnumName = () => {
  const t = useTranslations("useEnumName")

  return (category: CategoryIdentifier) => {
    switch (category) {
      case "ART":
        return t("art")
      case "GAMING":
        return t("gaming")
      case "MEMBERSHIPS":
        return t("memberships")
      case "MUSIC":
        return t("music")
      case "PFPS":
        return t("pfps")
      case "PHOTOGRAPHY":
        return t("photography")
      case "SPORTS_COLLECTIBLES":
        return t("sportsCollectibles")
      case "VIRTUAL_WORLDS":
        return t("virtualWorlds")
      case "DOMAIN_NAMES":
        return t("domainNames")
      case "UNSPECIFIED":
        return t("unspecified")
    }
  }
}

export const useTimeframeEnumName = () => {
  const t = useTranslations("useEnumName")

  return (timeframe: Timeframe) => {
    switch (timeframe) {
      case "ONE_MINUTE":
        return t("oneMinute")
      case "FIVE_MINUTE":
        return t("fiveMinute")
      case "FIFTEEN_MINUTE":
        return t("fifteenMinute")
      case "ONE_HOUR":
        return t("oneHour")
      case "ONE_DAY":
        return t("oneDay")
      case "SEVEN_DAYS":
        return t("sevenDays")
      case "THIRTY_DAYS":
        return t("thirtyDays")
      case "ONE_YEAR":
        return t("oneYear")
      case "ALL_TIME":
        return t("allTime")
    }
  }
}

export const useLongTimeframeEnumName = () => {
  const t = useTranslations("useEnumName")

  return (timeframe: Timeframe) => {
    switch (timeframe) {
      case "ALL_TIME":
        return t("allTimeLong")
      case "ONE_YEAR":
        return t("oneYearLong")
      case "THIRTY_DAYS":
        return t("thirtyDaysLong")
      case "SEVEN_DAYS":
        return t("sevenDaysLong")
      case "ONE_DAY":
        return t("oneDayLong")
      case "ONE_HOUR":
        return t("oneHourLong")
      case "FIFTEEN_MINUTE":
        return t("fifteenMinuteLong")
      case "FIVE_MINUTE":
        return t("fiveMinuteLong")
      case "ONE_MINUTE":
        return t("oneMinuteLong")
    }
  }
}

export const useCandleSizeEnumName = () => {
  const t = useTranslations("useEnumName")

  return (candleSize: CandleBucketSize) => {
    switch (candleSize) {
      case "ONE_MINUTE":
        return t("oneMinute")
      case "FIVE_MINUTES":
        return t("fiveMinutes")
      case "FIFTEEN_MINUTES":
        return t("fifteenMinutes")
      case "ONE_HOUR":
        return t("oneHour")
      case "FOUR_HOURS":
        return t("fourHours")
      case "ONE_DAY":
        return t("oneDay")
    }
  }
}

export const useLongCandleSizeEnumName = () => {
  const t = useTranslations("useEnumName")

  return (candleSize: CandleBucketSize) => {
    switch (candleSize) {
      case "ONE_MINUTE":
        return t("oneMinuteLong")
      case "FIVE_MINUTES":
        return t("fiveMinutesLong")
      case "FIFTEEN_MINUTES":
        return t("fifteenMinutesLong")
      case "ONE_HOUR":
        return t("oneHourLong")
      case "FOUR_HOURS":
        return t("fourHoursLong")
      case "ONE_DAY":
        return t("oneDayLong")
    }
  }
}

export const useOfferTypeEnumName = () => {
  const t = useTranslations("useEnumName")

  return (offerType: OfferType) => {
    switch (offerType) {
      case "BASIC":
        return t("basic")
      case "CRITERIA_TRAIT":
        return t("criteriaTrait")
      case "CRITERIA_COLLECTION":
        return t("criteriaCollection")
    }
  }
}

export const useEditableCollectionsTypeEnumName = () => {
  const t = useTranslations("useEnumName")

  return (type: EditableCollectionsType) => {
    switch (type) {
      case "COLLECTION":
        return t("collection")
      case "DROP":
        return t("drop")
    }
  }
}

export const useEditableCollectionsStatusEnumName = () => {
  const t = useTranslations("useEnumName")

  return (status: EditableCollectionsStatus) => {
    switch (status) {
      case "PUBLISHED":
        return t("published")
      case "DRAFT":
        return t("draft")
      case "MINTING":
        return t("minting")
      case "MINT_ENDED":
        return t("mintEnded")
    }
  }
}

export const useContractStandardEnumName = () => {
  const t = useTranslations("useEnumName")

  return (status: ContractStandard) => {
    switch (status) {
      case "CRYPTOPUNKS":
        return t("cryptopunks")
      case "ERC20":
        return t("erc20")
      case "ERC721":
        return t("erc721")
      case "ERC1155":
        return t("erc1155")
      case "INSCRIPTIONS":
        return t("inscriptions")
      case "NATIVE":
        return t("native")
      case "RUNES":
        return t("runes")
    }
  }
}
